import { useEffect, useState } from "react";
import {
  type FingerspellingEntry,
  type FingerspellingSide,
  getFingerspellingImageUrl,
  getYuraiImageUrl,
} from "../../lib/fingerspelling";
import { hasYuraiHint } from "../../lib/fingerspelling-layout";
import { CaptionText } from "./CaptionText";

type FingerspellingDetailModalProps = {
  entry: FingerspellingEntry;
  side: FingerspellingSide;
  onClose: () => void;
};

/** 50音表の1枚を拡大して、説明文とヒント（あいて編のみ）を見せるモーダル */
export const FingerspellingDetailModal = ({
  entry,
  side,
  onClose,
}: FingerspellingDetailModalProps) => {
  const [showHint, setShowHint] = useState(false);

  // 表示中は背景をスクロールさせない
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const canShowHint = side === "aite" && hasYuraiHint(entry.id);
  const src =
    showHint && canShowHint
      ? getYuraiImageUrl(entry.id)
      : getFingerspellingImageUrl(side, entry.id);

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center p-4">
      <button
        type="button"
        aria-hidden="true"
        data-testid="modal-backdrop"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/50"
      />
      <dialog
        open
        aria-modal="true"
        aria-label={`${entry.kana}の指文字`}
        className="relative m-0 w-full max-w-sm select-none rounded-2xl border-0 bg-white p-4 text-center text-neutral-900 [-webkit-touch-callout:none]"
      >
        <button
          type="button"
          aria-label="とじる"
          onClick={onClose}
          className="absolute right-2 top-2 touch-manipulation px-2 py-1 text-xl text-neutral-500"
        >
          ✕
        </button>
        <div className="text-5xl font-bold">{entry.kana}</div>
        <img
          src={src}
          alt={entry.kana}
          draggable={false}
          className="mx-auto mt-2 block w-full max-w-xs"
        />
        <p className="mt-2 text-sm text-neutral-700">
          <CaptionText segments={entry.caption[side]} />
        </p>
        {canShowHint && (
          <button
            type="button"
            onClick={() => setShowHint((shown) => !shown)}
            className="mt-3 touch-manipulation rounded-xl bg-neutral-900 px-4 py-2 text-sm text-white"
          >
            {showHint ? "ヒントを消す" : "ヒントを見る"}
          </button>
        )}
      </dialog>
    </div>
  );
};
